/**
 * Marple & Co Design System - Option B Aesthetic
 * Comprehensive styling rules for consistent UI development
 */

// ============================================
// COLOR PALETTE
// ============================================

export const colors = {
  // Primary Colors
  primary: {
    DEFAULT: '#061B37',      // Deep Navy
    50: '#E8EDF3',
    100: '#D1DBE7',
    200: '#A3B7CF',
    300: '#7593B7',
    400: '#476F9F',
    500: '#061B37',          // Base
    600: '#05162E',
    700: '#041226',
    800: '#030D1D',
    900: '#020913',
  },
  
  // Accent Colors
  accent: {
    DEFAULT: '#F7971C',      // Marple Orange
    50: '#FEF4E6',
    100: '#FDE9CD',
    200: '#FBD39B',
    300: '#F9BD69',
    400: '#F7A737',
    500: '#F7971C',          // Base
    600: '#D47D13',
    700: '#B1630F',
    800: '#8E4A0B',
    900: '#6B3207',
  },
  
  // Brand Colors
  brand: {
    DEFAULT: '#9FC131',      // Marple Green
    50: '#F4F8E6',
    100: '#E9F1CD',
    200: '#D3E39B',
    300: '#BDD569',
    400: '#A7C737',
    500: '#9FC131',          // Base
    600: '#84A229',
    700: '#698321',
    800: '#4E6419',
    900: '#334511',
  },
  
  // Supporting Colors
  neutral: {
    white: '#FFFFFF',
    50: '#F8F9FA',
    100: '#F1F3F5',
    200: '#E9ECEF',
    300: '#DEE2E6',
    400: '#CED4DA',
    500: '#ADB5BD',
    600: '#868E96',
    700: '#495057',
    800: '#343A40',
    900: '#212529',
    black: '#000000',
  },
  
  // Semantic Colors
  success: '#28A745',
  warning: '#FFC107',
  error: '#DC3545',
  info: '#17A2B8',
} as const;

// ============================================
// TYPOGRAPHY
// ============================================

export const typography = {
  // Font Families
  fonts: {
    heading: "'Novecento Sans', 'Montserrat', sans-serif",
    body: "'Avenir Next', 'Lato', sans-serif",
    quote: "'EB Garamond', 'Garamond', serif",
    mono: "'SF Mono', 'Monaco', monospace",
  },
  
  // Font Sizes (in rem)
  sizes: {
    xs: '0.75rem',      // 12px
    sm: '0.875rem',     // 14px
    base: '1rem',       // 16px
    lg: '1.125rem',     // 18px
    xl: '1.25rem',      // 20px
    '2xl': '1.5rem',    // 24px
    '3xl': '1.875rem',  // 30px
    '4xl': '2.25rem',   // 36px
    '5xl': '3rem',      // 48px
    '6xl': '3.75rem',   // 60px
    '7xl': '4.5rem',    // 72px
  },
  
  // Font Weights
  weights: {
    light: 300,
    normal: 400,
    medium: 500,
    semibold: 600,
    bold: 700,
    extrabold: 800,
  },
  
  // Line Heights
  lineHeights: {
    none: 1,
    tight: 1.25,
    snug: 1.375,
    normal: 1.5,
    relaxed: 1.625,
    loose: 2,
  },
  
  // Letter Spacing
  letterSpacing: {
    tighter: '-0.05em',
    tight: '-0.025em',
    normal: '0',
    wide: '0.025em',
    wider: '0.05em',
    widest: '0.1em',
  },
} as const;

// ============================================
// SPACING
// ============================================

export const spacing = {
  0: '0',
  1: '0.25rem',   // 4px
  2: '0.5rem',    // 8px
  3: '0.75rem',   // 12px
  4: '1rem',      // 16px
  5: '1.25rem',   // 20px
  6: '1.5rem',    // 24px
  8: '2rem',      // 32px
  10: '2.5rem',   // 40px
  12: '3rem',     // 48px
  16: '4rem',     // 64px
  20: '5rem',     // 80px
  24: '6rem',     // 96px
  32: '8rem',     // 128px
  40: '10rem',    // 160px
  48: '12rem',    // 192px
  56: '14rem',    // 224px
  64: '16rem',    // 256px
} as const;

// ============================================
// SHADOWS
// ============================================

export const shadows = {
  // Soft, organic shadows as specified
  sm: '0 2px 8px rgba(6, 27, 55, 0.04)',
  DEFAULT: '0 10px 30px rgba(6, 27, 55, 0.08)',
  md: '0 10px 30px rgba(6, 27, 55, 0.08)',
  lg: '0 20px 40px rgba(6, 27, 55, 0.12)',
  xl: '0 25px 50px rgba(6, 27, 55, 0.15)',
  
  // Specialized shadows
  card: '0 10px 30px rgba(6, 27, 55, 0.08)',
  button: '0 4px 12px rgba(6, 27, 55, 0.1)',
  hover: '0 15px 35px rgba(6, 27, 55, 0.12)',
  elevated: '0 20px 40px rgba(6, 27, 55, 0.15)',
  
  // Colored shadows
  accent: '0 10px 30px rgba(247, 151, 28, 0.2)',
  brand: '0 10px 30px rgba(159, 193, 49, 0.2)',
} as const;

// ============================================
// BORDER RADIUS
// ============================================

export const radius = {
  none: '0',
  sm: '4px',
  DEFAULT: '12px',      // Cards
  md: '12px',
  lg: '16px',
  xl: '24px',
  '2xl': '32px',
  full: '9999px',       // Pill shape for buttons
  pill: '9999px',
} as const;

// ============================================
// TRANSITIONS
// ============================================

export const transitions = {
  // Durations
  duration: {
    fast: '150ms',
    normal: '200ms',
    slow: '300ms',
    slower: '500ms',
  },
  
  // Easings
  easing: {
    DEFAULT: 'cubic-bezier(0.4, 0, 0.2, 1)',
    in: 'cubic-bezier(0.4, 0, 1, 1)',
    out: 'cubic-bezier(0, 0, 0.2, 1)',
    bounce: 'cubic-bezier(0.68, -0.55, 0.265, 1.55)',
  },
  
  // Common transitions
  default: 'all 200ms cubic-bezier(0.4, 0, 0.2, 1)',
  transform: 'transform 200ms cubic-bezier(0.4, 0, 0.2, 1)',
  colors: 'color, background-color, border-color 200ms cubic-bezier(0.4, 0, 0.2, 1)',
  shadow: 'box-shadow 200ms cubic-bezier(0.4, 0, 0.2, 1)',
} as const;

// ============================================
// Z-INDEX SCALE
// ============================================

export const zIndex = {
  hide: -1,
  auto: 'auto',
  base: 0,
  docked: 10,
  dropdown: 1000,
  sticky: 1100,
  banner: 1200,
  overlay: 1300,
  modal: 1400,
  popover: 1500,
  skipLink: 1600,
  toast: 1700,
  tooltip: 1800,
} as const;

// ============================================
// BREAKPOINTS
// ============================================

export const breakpoints = {
  sm: '640px',
  md: '768px',
  lg: '1024px',
  xl: '1280px',
  '2xl': '1536px',
} as const;

// ============================================
// COMPONENT STYLES
// ============================================

export const components = {
  // Button Styles
  button: {
    base: `
      inline-flex items-center justify-center gap-2
      px-6 py-3
      font-family: ${typography.fonts.heading}
      font-weight: ${typography.weights.semibold}
      font-size: ${typography.sizes.sm}
      text-transform: uppercase
      letter-spacing: ${typography.letterSpacing.wider}
      rounded-full
      transition-all duration-200
      focus:outline-none focus:ring-2 focus:ring-offset-2
      disabled:opacity-50 disabled:cursor-not-allowed
    `,
    
    variants: {
      primary: `
        bg-[${colors.primary.DEFAULT}] text-white
        shadow-[${shadows.button}]
        hover:bg-[${colors.primary[600]}]
        hover:shadow-[${shadows.hover}]
        hover:-translate-y-0.5
        focus:ring-[${colors.primary.DEFAULT}]
      `,
      
      accent: `
        bg-[${colors.accent.DEFAULT}] text-white
        shadow-[${shadows.accent}]
        hover:bg-[${colors.accent[600]}]
        hover:shadow-[${shadows.hover}]
        hover:-translate-y-0.5
        focus:ring-[${colors.accent.DEFAULT}]
      `,
      
      brand: `
        bg-[${colors.brand.DEFAULT}] text-[${colors.primary.DEFAULT}]
        shadow-[${shadows.brand}]
        hover:bg-[${colors.brand[600]}]
        hover:shadow-[${shadows.hover}]
        hover:-translate-y-0.5
        focus:ring-[${colors.brand.DEFAULT}]
      `,
      
      outline: `
        bg-transparent
        border-2 border-[${colors.primary.DEFAULT}]
        text-[${colors.primary.DEFAULT}]
        hover:bg-[${colors.primary.DEFAULT}]
        hover:text-white
        focus:ring-[${colors.primary.DEFAULT}]
      `,
      
      ghost: `
        bg-transparent
        text-[${colors.primary.DEFAULT}]
        hover:bg-[${colors.primary[50]}]
        focus:ring-[${colors.primary.DEFAULT}]
      `,
    },
    
    sizes: {
      sm: 'px-4 py-2 text-xs',
      md: 'px-6 py-3 text-sm',
      lg: 'px-8 py-4 text-base',
    },
  },
  
  // Card Styles
  card: {
    base: `
      bg-white
      rounded-[${radius.DEFAULT}]
      shadow-[${shadows.card}]
      overflow-hidden
      transition-all duration-300
    `,
    
    hover: `
      hover:shadow-[${shadows.hover}]
      hover:-translate-y-1
    `,
    
    variants: {
      default: 'border border-gray-100',
      elevated: 'shadow-[${shadows.elevated}]',
      flat: 'shadow-none border border-gray-200',
    },
  },
  
  // Input Styles
  input: {
    base: `
      w-full
      px-4 py-3
      bg-white
      border-2 border-gray-200
      rounded-[${radius.DEFAULT}]
      font-family: ${typography.fonts.body}
      font-size: ${typography.sizes.base}
      text-[${colors.neutral[800]}]
      placeholder:text-[${colors.neutral[400]}]
      transition-all duration-200
      focus:outline-none
      focus:border-[${colors.primary.DEFAULT}]
      focus:ring-4 focus:ring-[${colors.primary[100]}]
      disabled:bg-[${colors.neutral[100]}]
      disabled:cursor-not-allowed
    `,
  },
  
  // Badge Styles
  badge: {
    base: `
      inline-flex items-center gap-1.5
      px-3 py-1
      rounded-full
      text-xs font-semibold
      uppercase tracking-wide
    `,
    
    variants: {
      primary: `bg-[${colors.primary[100]}] text-[${colors.primary.DEFAULT}]`,
      accent: `bg-[${colors.accent[100]}] text-[${colors.accent[800]}]`,
      brand: `bg-[${colors.brand[100]}] text-[${colors.brand[800]}]`,
      neutral: `bg-[${colors.neutral[100]}] text-[${colors.neutral[700]}]`,
    },
  },
} as const;

// ============================================
// GRAIN TEXTURE OVERLAY
// ============================================

export const grainTexture = {
  // CSS for grain overlay
  styles: `
    position: relative;
  `,
  
  overlay: `
    &::before {
      content: '';
      position: absolute;
      inset: 0;
      background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.8' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E");
      opacity: 0.02;
      pointer-events: none;
      z-index: 1;
    }
  `,
  
  // Tailwind class approach
  className: 'relative before:absolute before:inset-0 before:bg-[url("data:image/svg+xml,%3Csvg%20viewBox%3D%270%200%20256%20256%27%20xmlns%3D%27http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%27%3E%3Cfilter%20id%3D%27noise%27%3E%3CfeTurbulence%20type%3D%27fractalNoise%27%20baseFrequency%3D%270.8%27%20numOctaves%3D%274%27%20stitchTiles%3D%27stitch%27%2F%3E%3C%2Ffilter%3E%3Crect%20width%3D%27100%25%27%20height%3D%27100%25%27%20filter%3D%27url%28%23noise%29%27%2F%3E%3C%2Fsvg%3E")] before:opacity-[0.02] before:pointer-events-none before:z-[1]',
} as const;

// ============================================
// UTILITY FUNCTIONS
// ============================================

/**
 * Generate CSS custom properties for the design system
 */
export function generateCSSVariables(): string {
  return `
    :root {
      /* Primary Colors */
      --color-primary: ${colors.primary.DEFAULT};
      --color-primary-50: ${colors.primary[50]};
      --color-primary-100: ${colors.primary[100]};
      --color-primary-600: ${colors.primary[600]};
      
      /* Accent Colors */
      --color-accent: ${colors.accent.DEFAULT};
      --color-accent-50: ${colors.accent[50]};
      --color-accent-100: ${colors.accent[100]};
      --color-accent-600: ${colors.accent[600]};
      
      /* Brand Colors */
      --color-brand: ${colors.brand.DEFAULT};
      --color-brand-50: ${colors.brand[50]};
      --color-brand-100: ${colors.brand[100]};
      --color-brand-600: ${colors.brand[600]};
      
      /* Typography */
      --font-heading: ${typography.fonts.heading};
      --font-body: ${typography.fonts.body};
      --font-quote: ${typography.fonts.quote};
      
      /* Shadows */
      --shadow-sm: ${shadows.sm};
      --shadow-md: ${shadows.md};
      --shadow-lg: ${shadows.lg};
      --shadow-card: ${shadows.card};
      --shadow-button: ${shadows.button};
      --shadow-hover: ${shadows.hover};
      
      /* Radius */
      --radius-sm: ${radius.sm};
      --radius-md: ${radius.md};
      --radius-lg: ${radius.lg};
      --radius-full: ${radius.full};
    }
  `;
}

/**
 * Get color with opacity
 */
export function withOpacity(color: string, opacity: number): string {
  // Convert hex to rgba
  const hex = color.replace('#', '');
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${opacity})`;
}

/**
 * Create a gradient from primary colors
 */
export function createGradient(direction: string = 'to right', ...colorStops: string[]): string {
  return `linear-gradient(${direction}, ${colorStops.join(', ')})`;
}

// ============================================
// EXPORT DEFAULT
// ============================================

export default {
  colors,
  typography,
  spacing,
  shadows,
  radius,
  transitions,
  zIndex,
  breakpoints,
  components,
  grainTexture,
  generateCSSVariables,
  withOpacity,
  createGradient,
};
